import { isEmpty } from 'es-toolkit/compat';
import { z } from 'zod';

import { ActiveInactive, IntegrationScope, IntegrationWhere } from 'src/api';
import { FilterItem } from 'src/providers/FilterStorageProvider/types';

export type RateLimitUnit = 'sec' | 'min' | 'hour';

export const RATE_LIMIT_UNIT_MS: Record<RateLimitUnit, number> = {
  sec: 1_000,
  min: 60_000,
  hour: 3_600_000,
};

export const RATE_LIMIT_UNIT_OPTIONS: ReadonlyArray<{ value: RateLimitUnit; label: string }> = [
  { value: 'sec', label: 'seconds' },
  { value: 'min', label: 'minutes' },
  { value: 'hour', label: 'hours' },
];

const RATE_LIMIT_UNIT_SHORT: Record<RateLimitUnit, string> = {
  sec: 'sec',
  min: 'min',
  hour: 'hr',
};

export const msToRateLimitDuration = (ms: number): { value: number; unit: RateLimitUnit } => {
  if (!ms || ms <= 0) return { value: 0, unit: 'min' };
  if (ms % RATE_LIMIT_UNIT_MS.hour === 0) {
    return { value: ms / RATE_LIMIT_UNIT_MS.hour, unit: 'hour' };
  }
  if (ms % RATE_LIMIT_UNIT_MS.min === 0) {
    return { value: ms / RATE_LIMIT_UNIT_MS.min, unit: 'min' };
  }
  return { value: ms / RATE_LIMIT_UNIT_MS.sec, unit: 'sec' };
};

export const rateLimitDurationToMs = (value: number, unit: RateLimitUnit): number =>
  Math.max(0, Math.round(value * RATE_LIMIT_UNIT_MS[unit]));

export const formatRateLimit = (attempts: number, windowMs: number): string => {
  if (!attempts || attempts <= 0 || !windowMs || windowMs <= 0) return 'Off';
  const { value, unit } = msToRateLimitDuration(windowMs);
  return `${attempts} / ${value} ${RATE_LIMIT_UNIT_SHORT[unit]}`;
};

const filterSchema = z.object({
  status: z.array(z.enum(ActiveInactive)).nullish(),
  scope: z.array(z.enum(IntegrationScope)).nullish(),
});

export const prepareIntegrationsFilter = (filter: FilterItem): IntegrationWhere | undefined => {
  if (isEmpty(filter)) return undefined;

  const where: IntegrationWhere = {};

  const parsedFilter = filterSchema.safeParse(filter).data;

  if (parsedFilter?.status && !isEmpty(parsedFilter.status)) {
    where.status = { in: parsedFilter.status };
  }

  if (parsedFilter?.scope && !isEmpty(parsedFilter.scope)) {
    where.scope = { hasSome: parsedFilter.scope };
  }

  return isEmpty(where) ? undefined : where;
};
